/* iter55z-audit round 9 — Companion (Indie Reader) admin panel.
 *
 * Ships four things:
 *   1. Diagnostics — one-click connectivity smoke test with a
 *      green/red badge. Calls /admin/indie-reader/diagnostics
 *      and (on success) /admin/indie-reader/books to prove the
 *      service key works end-to-end.
 *   2. Books — list, publish, delete inline. New-book form up top.
 *   3. Chapters — pick a book, view chapters, drag-reorder handles,
 *      inline edit + delete, add new.
 *   4. Uploads + Bulk import — cover / portrait dropzones and a
 *      Markdown manuscript importer.
 *
 * Every mutating call goes through the /api/admin/indie-reader/*
 * proxy, which stamps X-Idempotency-Key on the outbound request so
 * flaky-network double-clicks can't double-write upstream.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, Loader2, RefreshCw, CheckCircle2, XCircle, BookOpen, Trash2,
  Upload, Image as ImageIcon, ArrowUp, ArrowDown, FilePlus, Save,
  FileText, ChevronRight, ExternalLink, Bell, UserCheck, Users as UsersIcon,
  BarChart2, Check, X,
} from 'lucide-react';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

import { openConfirm } from '@/components/admin/ConfirmDialog';

/* ─── Diagnostics badge ───────────────────────────────────────────── */
function DiagnosticsCard() {
  const [busy,   setBusy]   = useState(false);
  const [status, setStatus] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const diag = await http.get('/admin/indie-reader/diagnostics');
      const ok = diag.data?.ok;
      let bookCount = null;
      if (ok) {
        try {
          const books = await http.get('/admin/indie-reader/books?include_deleted=false');
          bookCount = Array.isArray(books.data) ? books.data.length
                       : books.data?.books?.length ?? 0;
        } catch (_) { bookCount = null; }
      }
      setStatus({ ok, ...diag.data, book_count: bookCount, at: new Date().toISOString() });
    } catch (e) {
      setStatus({ ok: false, stage: 'panel',
                  message: e.response?.data?.detail || e.message,
                  at: new Date().toISOString() });
    } finally { setBusy(false); }
  };
  useEffect(() => { run(); }, []);   // eslint-disable-line

  const tone = status?.ok ? 'emerald' : status ? 'destructive' : 'neutral';
  return (
    <section data-testid="indie-diag-card" className="rounded-md border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <Activity className="h-3.5 w-3.5" /> Companion connectivity
        </div>
        <button
          onClick={run} disabled={busy}
          data-testid="indie-diag-run"
          className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-1 text-[11.5px] hover:bg-secondary/80 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Run check
        </button>
      </div>
      <div className={`rounded-md px-3 py-2 text-[12px]
        ${tone === 'emerald'    ? 'border border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
        : tone === 'destructive' ? 'border border-destructive/40 bg-destructive/10 text-destructive'
        : 'border border-border/60 bg-secondary/40 text-muted-foreground'}`}
      >
        <div className="flex items-center gap-2 font-semibold">
          {status?.ok ? <CheckCircle2 className="h-4 w-4" /> : status ? <XCircle className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
          <span data-testid="indie-diag-badge">
            {status?.ok
              ? `Healthy — service key accepted. ${status.book_count ?? 0} book(s) upstream.`
              : status?.message
                ? `Failed at "${status.stage}": ${status.message}`
                : 'Probing…'}
          </span>
        </div>
        {status?.base_url && (
          <div className="mt-1 font-mono text-[10.5px] opacity-75">Base: {status.base_url}</div>
        )}
      </div>
      {/* Open Companion App deep-link */}
      {status?.companion_url && (
        <div className="mt-3 flex items-center justify-between border-t border-border/50 pt-3">
          <span className="text-[11.5px] text-muted-foreground">
            Companion admin URL
          </span>
          <a
            href={`${(status.companion_url || '').replace(/\/api\/?$/, '')}/admin`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="indie-open-companion-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/8 px-3 py-1.5 text-[12px] font-medium text-primary hover:bg-primary/15 transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open Companion App
          </a>
        </div>
      )}
    </section>
  );
}

/* ─── Books CRUD ──────────────────────────────────────────────────── */
function BooksCard({ selectedId, onSelect, refreshTick }) {
  const [books, setBooks] = useState(null);
  const [busy,  setBusy]  = useState(false);
  const [draft, setDraft] = useState({ title: '', author_id: '', genre: '', blurb: '', price_cents: 299 });
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.get('/admin/indie-reader/books?include_deleted=false');
      const list = Array.isArray(r.data) ? r.data : (r.data?.books || []);
      setBooks(list);
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
      setBooks([]);
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshTick]);

  const create = async () => {
    if (!draft.title.trim()) return toast.error('Title required.');
    try {
      const r = await http.post('/admin/indie-reader/books', {
        title: draft.title, author_id: draft.author_id || null,
        genre: draft.genre || null, blurb: draft.blurb || null,
        price_cents: parseInt(draft.price_cents, 10) || 0,
      });
      toast.success(`Book "${r.data?.title || draft.title}" created.`);
      setDraft({ title: '', author_id: '', genre: '', blurb: '', price_cents: 299 });
      await load();
    } catch (e) {
      toast.error(`Create failed: ${e.response?.data?.detail || e.message}`);
    }
  };
  const publish = async (b, status) => {
    try {
      await http.post(`/admin/indie-reader/books/${b.id}/publish`, { status });
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const remove = async (b) => {
    if (!(await openConfirm({
      title: `Delete "${b.title}"?`,
      description: 'This is a soft-delete — the book stays in the Reader database as archived.',
      tone: 'destructive',
    }))) return;
    try {
      await http.delete(`/admin/indie-reader/books/${b.id}`);
      toast.success('Deleted.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <section data-testid="indie-books-card" className="rounded-md border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5" /> Books upstream
        </div>
        <button onClick={load} disabled={busy} data-testid="indie-books-refresh"
                className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-1 text-[11px] hover:bg-secondary/80">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh
        </button>
      </div>

      {/* New-book row */}
      <div className="mb-3 grid grid-cols-2 gap-2 rounded-md border border-border/60 bg-secondary/40 p-2 md:grid-cols-6">
        <input className="col-span-2 h-7 rounded-sm border border-border bg-background px-2 text-[11.5px]"
               placeholder="Title" value={draft.title}
               onChange={(e) => setDraft({ ...draft, title: e.target.value })}
               data-testid="indie-book-new-title" />
        <input className="h-7 rounded-sm border border-border bg-background px-2 text-[11.5px]"
               placeholder="author_id" value={draft.author_id}
               onChange={(e) => setDraft({ ...draft, author_id: e.target.value })} />
        <input className="h-7 rounded-sm border border-border bg-background px-2 text-[11.5px]"
               placeholder="genre" value={draft.genre}
               onChange={(e) => setDraft({ ...draft, genre: e.target.value })} />
        <input type="number" className="h-7 rounded-sm border border-border bg-background px-2 text-right font-mono text-[11.5px]"
               placeholder="299" value={draft.price_cents}
               onChange={(e) => setDraft({ ...draft, price_cents: e.target.value })} />
        <button onClick={create} data-testid="indie-book-new-create"
                className="inline-flex items-center justify-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90">
          <FilePlus className="h-3 w-3" /> Add
        </button>
      </div>

      {books === null ? (
        <div className="flex h-16 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : books.length === 0 ? (
        <div className="text-center text-[12px] text-muted-foreground">No books upstream yet.</div>
      ) : (
        <table className="w-full text-[12px]" data-testid="indie-books-table">
          <thead>
            <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground">
              <th className="py-2 pr-3">Title</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3 text-right">Chapters</th>
              <th className="py-2 pr-3 text-right">Price</th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {books.map((b) => (
              <tr key={b.id}
                  data-testid={`indie-book-row-${b.id}`}
                  className={`border-b border-border/60 ${selectedId === b.id ? 'bg-secondary/60' : ''}`}>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-2">
                    {/* iter55z-audit round 9 (extras) — Cover thumbnail.
                        Renders a small 24×32 image whenever the upstream
                        book has `cover_url`, else a soft placeholder. */}
                    {b.cover_url ? (
                      <img
                        src={b.cover_url} alt=""
                        data-testid={`indie-book-cover-${b.id}`}
                        className="h-8 w-6 flex-shrink-0 rounded-sm border border-border object-cover"
                      />
                    ) : (
                      <div className="flex h-8 w-6 flex-shrink-0 items-center justify-center rounded-sm border border-dashed border-border text-muted-foreground/50">
                        <ImageIcon className="h-3 w-3" />
                      </div>
                    )}
                    <button onClick={() => onSelect(b)} className="text-left hover:underline"
                            data-testid={`indie-book-select-${b.id}`}>
                      {b.title || '(untitled)'}
                      <ChevronRight className="ml-1 inline h-3 w-3 opacity-50" />
                    </button>
                  </div>
                </td>
                <td className="py-2 pr-3 font-mono text-[11px]">{b.status || '—'}</td>
                <td className="py-2 pr-3 text-right font-mono">{b.chapters_count ?? '—'}</td>
                <td className="py-2 pr-3 text-right font-mono">${((b.price_cents || 0) / 100).toFixed(2)}</td>
                <td className="py-2 pr-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => publish(b, 'available')} title="Publish" data-testid={`indie-book-pub-${b.id}`}
                            className="rounded-sm bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25">Live</button>
                    <button onClick={() => publish(b, 'draft')} title="Unpublish" data-testid={`indie-book-draft-${b.id}`}
                            className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] hover:bg-secondary/80">Draft</button>
                    <button onClick={() => remove(b)} title="Delete" data-testid={`indie-book-del-${b.id}`}
                            className="rounded-sm p-1 text-destructive hover:bg-destructive/10"><Trash2 className="h-3 w-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ─── Chapters (reorder handles) ──────────────────────────────────── */
function ChaptersCard({ book }) {
  const [chs,  setChs]  = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ title: '', body: '' });
  const load = useCallback(async () => {
    if (!book?.id) { setChs(null); return; }
    setBusy(true);
    try {
      const r = await http.get(`/admin/indie-reader/books/${book.id}/chapters`);
      setChs(Array.isArray(r.data) ? r.data : (r.data?.chapters || []));
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
      setChs([]);
    } finally { setBusy(false); }
  }, [book?.id]);
  useEffect(() => { load(); }, [load]);

  const move = async (idx, delta) => {
    if (!chs || !book?.id) return;
    const j = idx + delta;
    if (j < 0 || j >= chs.length) return;
    const order = chs.map((_, i) => i);
    [order[idx], order[j]] = [order[j], order[idx]];
    try {
      await http.put(`/admin/indie-reader/books/${book.id}/chapters/reorder`, { order });
      toast.success('Reordered.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const add = async () => {
    if (!draft.title.trim() || !draft.body.trim()) return toast.error('Title + body required.');
    try {
      await http.post(`/admin/indie-reader/books/${book.id}/chapters`, draft);
      toast.success('Chapter added.');
      setDraft({ title: '', body: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const del = async (idx) => {
    if (!(await openConfirm({
      title: `Delete chapter ${idx + 1}?`,
      tone: 'destructive',
    }))) return;
    try {
      await http.delete(`/admin/indie-reader/books/${book.id}/chapters/${idx}`);
      toast.success('Deleted.'); await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  if (!book) {
    return (
      <section className="rounded-md border border-dashed border-border bg-card p-4 text-center text-[12px] text-muted-foreground"
               data-testid="indie-chapters-empty">
        Pick a book above to manage its chapters.
      </section>
    );
  }
  return (
    <section data-testid="indie-chapters-card" className="rounded-md border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          Chapters — {book.title}
        </div>
        <button onClick={load} disabled={busy} data-testid="indie-chapters-refresh"
                className="rounded-sm bg-secondary px-2 py-1 text-[11px] hover:bg-secondary/80">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
        </button>
      </div>

      {chs === null ? (
        <div className="flex h-16 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : chs.length === 0 ? (
        <div className="text-[12px] text-muted-foreground">No chapters yet.</div>
      ) : (
        <ul className="mb-3 space-y-1" data-testid="indie-chapters-list">
          {chs.map((c, i) => (
            <li key={i}
                data-testid={`indie-chapter-${i}`}
                className="flex items-center gap-2 rounded-sm border border-border/60 px-2 py-1 text-[12px]">
              <span className="w-6 font-mono text-muted-foreground">{i + 1}.</span>
              <span className="flex-1 truncate">{c.title || '(untitled)'}</span>
              <button onClick={() => move(i, -1)} disabled={i === 0}
                      data-testid={`indie-chapter-up-${i}`}
                      className="rounded-sm p-1 hover:bg-secondary disabled:opacity-30">
                <ArrowUp className="h-3 w-3" />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === chs.length - 1}
                      data-testid={`indie-chapter-down-${i}`}
                      className="rounded-sm p-1 hover:bg-secondary disabled:opacity-30">
                <ArrowDown className="h-3 w-3" />
              </button>
              <button onClick={() => del(i)} data-testid={`indie-chapter-del-${i}`}
                      className="rounded-sm p-1 text-destructive hover:bg-destructive/10">
                <Trash2 className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-md border border-border/60 bg-secondary/40 p-2">
        <div className="mb-1 text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">Add a chapter</div>
        <input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          placeholder="Chapter title"
          data-testid="indie-chapter-new-title"
          className="mb-1 h-7 w-full rounded-sm border border-border bg-background px-2 text-[11.5px]"
        />
        <textarea
          value={draft.body}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          placeholder="Chapter body"
          rows={5}
          data-testid="indie-chapter-new-body"
          className="mb-1 w-full rounded-sm border border-border bg-background p-2 text-[11.5px] font-mono"
        />
        <button onClick={add} data-testid="indie-chapter-new-add"
                className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90">
          <Save className="h-3 w-3" /> Add chapter
        </button>
      </div>
    </section>
  );
}

/* ─── Uploads (cover + portrait dropzones) ────────────────────────── */
function UploadCard({ kind, book, onAttachedToBook }) {
  // `kind` = 'cover' | 'portrait'
  const [result, setResult] = useState(null);
  const [busy,   setBusy]   = useState(false);
  const [attaching, setAttaching] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await http.post(`/admin/indie-reader/uploads/${kind}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(`${kind} uploaded.`);
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };
  const attach = async () => {
    if (!book?.id || !result?.url) return;
    setAttaching(true);
    try {
      await http.put(`/admin/indie-reader/books/${book.id}`, { cover_url: result.url });
      toast.success(`Attached cover to "${book.title}".`);
      if (onAttachedToBook) onAttachedToBook(book.id, result.url);
    } catch (e) {
      toast.error(`Attach failed: ${e.response?.data?.detail || e.message}`);
    } finally { setAttaching(false); }
  };
  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid={`indie-upload-${kind}`}>
      <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        <ImageIcon className="h-3.5 w-3.5" /> Upload {kind}
      </div>
      <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border/60 bg-secondary/30 p-4 text-[12px] hover:border-primary/60"
             onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
             onDragOver={(e) => e.preventDefault()}>
        <Upload className="h-5 w-5 text-muted-foreground" />
        <span className="text-muted-foreground">
          {busy ? 'Uploading…' : `Drag a ${kind} here or click to pick`}
        </span>
        <input type="file" hidden accept="image/*"
               data-testid={`indie-upload-${kind}-input`}
               onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {result && (
        <div className="mt-2 space-y-2" data-testid={`indie-upload-${kind}-result`}>
          <div className="flex items-center gap-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-800 dark:text-emerald-300">
            <CheckCircle2 className="h-3 w-3" />
            <a href={result.url} target="_blank" rel="noreferrer" className="truncate hover:underline">
              {result.path}
            </a>
          </div>
          {/* iter55z-audit round 9 (extras) — Attach the just-uploaded
              cover to the selected book so the thumbnail appears in the
              books table without a manual PUT. Only shown for `cover`. */}
          {kind === 'cover' && book?.id && (
            <button
              onClick={attach} disabled={attaching}
              data-testid="indie-upload-cover-attach"
              className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {attaching ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
              Attach to "{book.title}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Bulk import (Markdown) ──────────────────────────────────────── */
function BulkImportCard({ book }) {
  const [markdown, setMarkdown]   = useState('');
  const [busy,     setBusy]       = useState(false);
  const [report,   setReport]     = useState(null);
  // iter55z-audit round 9 (extras) — Admin toggle to force-regenerate
  // idempotency keys on the next push. Handy when Indie Reader
  // legitimately rejected a request and the admin wants a clean retry.
  const [forceRegen, setForceRegen] = useState(false);
  const preview = useMemo(() => {
    if (!markdown.trim()) return [];
    const hits = [...markdown.matchAll(/^(#{1,2})\s+(.+)$/gm)];
    if (hits.length === 0) return [{ title: 'Chapter 1', ok: true }];
    return hits.map((m) => ({ title: m[2].trim(), ok: true }));
  }, [markdown]);
  const push = async () => {
    if (!book?.id) return toast.error('Pick a book first.');
    if (!markdown.trim()) return toast.error('Paste a manuscript first.');
    setBusy(true); setReport(null);
    try {
      const r = await http.post(`/admin/indie-reader/books/${book.id}/bulk-import`, {
        book_id: book.id, markdown, split_on_headings: true,
        force_regenerate_key: forceRegen,
        // Reuse the previous prefix if we're retrying (unless forced).
        idempotency_prefix: !forceRegen && report?.idempotency_prefix
          ? report.idempotency_prefix : undefined,
      });
      setReport(r.data);
      if (r.data?.ok) toast.success(`Imported ${r.data.imported} chapter(s).`);
      else            toast.error(`Stopped after ${r.data?.imported ?? 0} chapters.`);
      // After a successful push, disable force-regen so the next retry
      // picks up the newly-issued prefix by default.
      if (r.data?.ok) setForceRegen(false);
    } catch (e) {
      toast.error(`Import failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };
  return (
    <section data-testid="indie-bulk-card" className="rounded-md border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        <FileText className="h-3.5 w-3.5" /> Bulk chapter import (Markdown)
      </div>
      <div className="mb-2 text-[11.5px] text-muted-foreground">
        Split the manuscript on <code className="rounded-sm bg-secondary px-1"># heading</code> or
        <code className="rounded-sm bg-secondary px-1">## heading</code>. Each heading becomes one
        chapter title upstream. Retries are safe — every chapter is stamped with an idempotency key.
      </div>
      <textarea
        value={markdown}
        onChange={(e) => setMarkdown(e.target.value)}
        placeholder={"# Chapter 1 — The Opening\n\nProse goes here…\n\n# Chapter 2 — Rising Action\n\nMore prose…"}
        rows={12}
        data-testid="indie-bulk-textarea"
        className="mb-2 w-full rounded-sm border border-border bg-background p-2 font-mono text-[11.5px]"
      />
      <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        Detected chapters: <b className="text-foreground" data-testid="indie-bulk-preview-count">{preview.length}</b>
        {preview.slice(0, 3).map((c, i) => (
          <span key={i} className="max-w-[10rem] truncate rounded-sm bg-secondary px-1.5 py-0.5 text-[10.5px]">
            {c.title}
          </span>
        ))}
        {preview.length > 3 && <span>+ {preview.length - 3} more</span>}
        <label className="ml-auto flex cursor-pointer items-center gap-1.5 rounded-sm border border-border/60 bg-secondary/40 px-2 py-0.5 text-[11px]"
               title="Ignore the previous idempotency prefix and generate a brand-new one so upstream treats every chapter as a fresh POST.">
          <input
            type="checkbox"
            checked={forceRegen}
            onChange={(e) => setForceRegen(e.target.checked)}
            data-testid="indie-bulk-force-regen"
            className="accent-primary"
          />
          <span className={forceRegen ? 'font-semibold text-destructive' : ''}>
            Force fresh key on retry
          </span>
        </label>
      </div>
      <button
        onClick={push} disabled={busy || !book?.id || preview.length === 0}
        data-testid="indie-bulk-push"
        className="inline-flex items-center gap-1 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
        {book ? `Push to "${book.title}"` : 'Pick a book first'}
      </button>
      {report && (
        <div className="mt-3 rounded-md border border-border/60 bg-secondary/30 p-2 text-[11px]"
             data-testid="indie-bulk-report">
          <b>{report.ok ? 'Import complete.' : 'Import stopped.'}</b>{' '}
          {report.imported}/{report.total} chapters pushed. Prefix: <code className="font-mono">{report.idempotency_prefix}</code>
        </div>
      )}
    </section>
  );
}

/* ─── Alerts ─────────────────────────────────────────────────────── */
function AlertsCard() {
  const [alerts, setAlerts] = useState(null);
  const [busy,   setBusy]   = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.get('/admin/indie-reader/alerts');
      // Companion returns {items:[...], unread:N} or a plain array.
      setAlerts(Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.alerts || []));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      // companion might not implement alerts yet — degrade gracefully
      if (e.response?.status === 404 || e.response?.status === 501) {
        setAlerts([]);
      } else {
        toast.error(`Alerts: ${msg}`);
        setAlerts([]);
      }
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ack = async (id) => {
    try {
      await http.post(`/admin/indie-reader/alerts/${id}/ack`);
      toast.success('Alert acknowledged.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const active = (alerts || []).filter((a) => !a.acked_at && !a.resolved_at);
  const tone = active.length > 0 ? 'amber' : 'neutral';

  return (
    <section data-testid="indie-alerts-card" className="rounded-md border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <Bell className="h-3.5 w-3.5" />
          Companion alerts
          {active.length > 0 && (
            <span className="ml-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9.5px] font-bold text-white">
              {active.length}
            </span>
          )}
        </div>
        <button onClick={load} disabled={busy} data-testid="indie-alerts-refresh"
                className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-1 text-[11px] hover:bg-secondary/80">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh
        </button>
      </div>
      {alerts === null ? (
        <div className="flex h-10 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : active.length === 0 ? (
        <div className={`rounded-sm border px-3 py-2 text-[12px] ${tone === 'neutral' ? 'border-emerald-500/30 bg-emerald-500/8 text-emerald-700 dark:text-emerald-300' : ''}`}>
          <CheckCircle2 className="mr-1.5 inline h-3.5 w-3.5" />
          No active alerts from the companion app.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="indie-alerts-list">
          {active.map((a) => (
            <li key={a.id || a._id}
                data-testid={`indie-alert-${a.id || a._id}`}
                className="flex items-start gap-2 rounded-sm border border-amber-500/30 bg-amber-500/8 px-3 py-2 text-[12px]">
              <Bell className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-300" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-amber-800 dark:text-amber-200">{a.title || a.kind || 'Alert'}</div>
                {a.message && <div className="mt-0.5 text-[11px] text-muted-foreground">{a.message}</div>}
              </div>
              <button onClick={() => ack(a.id || a._id)}
                      data-testid={`indie-alert-ack-${a.id || a._id}`}
                      className="shrink-0 rounded-sm bg-secondary px-1.5 py-0.5 text-[10.5px] hover:bg-secondary/80">
                Ack
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ─── Author Requests ────────────────────────────────────────────── */
function AuthorRequestsCard() {
  const [reqs,  setReqs]  = useState(null);
  const [busy,  setBusy]  = useState(false);
  const [filter, setFilter] = useState('pending');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.get(`/admin/indie-reader/author-requests?status=${filter}`);
      const list = Array.isArray(r.data) ? r.data : (r.data?.requests || r.data?.items || []);
      setReqs(list);
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 501) { setReqs([]); }
      else { toast.error(`Author requests: ${e.response?.data?.detail || e.message}`); setReqs([]); }
    } finally { setBusy(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await http.post(`/admin/indie-reader/author-requests/${id}/approve`);
      toast.success('Author request approved.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const reject = async (id) => {
    if (!(await openConfirm({ title: 'Reject this author request?', tone: 'destructive' }))) return;
    try {
      await http.post(`/admin/indie-reader/author-requests/${id}/reject`);
      toast.success('Request rejected.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <section data-testid="indie-author-req-card" className="rounded-md border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <UserCheck className="h-3.5 w-3.5" /> Author requests
        </div>
        <div className="flex items-center gap-1.5">
          {['pending','approved','rejected'].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
                    data-testid={`indie-author-req-filter-${s}`}
                    className={`rounded-sm px-2 py-0.5 text-[11px] capitalize ${filter === s ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'}`}>
              {s}
            </button>
          ))}
          <button onClick={load} disabled={busy} data-testid="indie-author-req-refresh"
                  className="rounded-sm bg-secondary p-1 hover:bg-secondary/80">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </button>
        </div>
      </div>
      {reqs === null ? (
        <div className="flex h-10 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : reqs.length === 0 ? (
        <div className="text-center text-[12px] text-muted-foreground py-2">No {filter} author requests.</div>
      ) : (
        <table className="w-full text-[12px]" data-testid="indie-author-req-table">
          <thead>
            <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground">
              <th className="py-2 pr-3">Name / Handle</th>
              <th className="py-2 pr-3">Email</th>
              <th className="py-2 pr-3">Requested</th>
              {filter === 'pending' && <th className="py-2 pr-3"></th>}
            </tr>
          </thead>
          <tbody>
            {reqs.map((req) => {
              const id = req.id || req._id;
              return (
                <tr key={id} data-testid={`indie-author-req-row-${id}`}
                    className="border-b border-border/60">
                  <td className="py-2 pr-3 font-medium">
                    {req.name || req.pen_name || req.username || '—'}
                    {req.handle && <span className="ml-1 text-[10.5px] text-muted-foreground">@{req.handle}</span>}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">{req.email || '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground font-mono text-[10.5px]">
                    {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}
                  </td>
                  {filter === 'pending' && (
                    <td className="py-2 pr-3">
                      <div className="inline-flex gap-1">
                        <button onClick={() => approve(id)} data-testid={`indie-author-req-approve-${id}`}
                                className="inline-flex items-center gap-0.5 rounded-sm bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25">
                          <Check className="h-2.5 w-2.5" /> Approve
                        </button>
                        <button onClick={() => reject(id)} data-testid={`indie-author-req-reject-${id}`}
                                className="inline-flex items-center gap-0.5 rounded-sm bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive hover:bg-destructive/20">
                          <X className="h-2.5 w-2.5" /> Reject
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ─── Users & Sales ──────────────────────────────────────────────── */
function UsersCard() {
  const [users,  setUsers]  = useState(null);
  const [sales,  setSales]  = useState(null);
  const [busy,   setBusy]   = useState(false);
  const [roleEditing, setRoleEditing] = useState({});

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [uRes, sRes] = await Promise.allSettled([
        http.get('/admin/indie-reader/users'),
        http.get('/admin/indie-reader/sales'),
      ]);
      if (uRes.status === 'fulfilled') {
        const d = uRes.value.data;
        setUsers(Array.isArray(d) ? d : (d?.users || d?.items || []));
      } else { setUsers([]); }
      if (sRes.status === 'fulfilled') setSales(sRes.value.data);
      else setSales(null);
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (userId, role) => {
    setRoleEditing((p) => ({ ...p, [userId]: true }));
    try {
      await http.post(`/admin/indie-reader/users/${userId}/role`, { role });
      toast.success(`Role → ${role}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setRoleEditing((p) => { const n = { ...p }; delete n[userId]; return n; });
    }
  };

  return (
    <section data-testid="indie-users-card" className="rounded-md border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <UsersIcon className="h-3.5 w-3.5" /> Reader accounts
        </div>
        <button onClick={load} disabled={busy} data-testid="indie-users-refresh"
                className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-1 text-[11px] hover:bg-secondary/80">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh
        </button>
      </div>

      {/* Sales summary strip — companion returns {totals:{revenue_cents,orders,units,titles}} */}
      {sales && (
        <div className="mb-3 flex flex-wrap gap-3 rounded-md border border-border/60 bg-secondary/30 px-3 py-2"
             data-testid="indie-sales-strip">
          {(() => {
            const t = sales.totals || sales;
            return [
              ['Revenue',  t.revenue_cents != null ? `$${(t.revenue_cents / 100).toFixed(2)}` : '—'],
              ['Orders',   t.orders ?? '—'],
              ['Units',    t.units  ?? '—'],
              ['Titles',   t.titles ?? '—'],
            ].map(([k, v]) => (
              <div key={k} className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
                <div className="font-mono text-[13px] font-semibold">{v}</div>
              </div>
            ));
          })()}
        </div>
      )}

      {users === null ? (
        <div className="flex h-10 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : users.length === 0 ? (
        <div className="text-center text-[12px] text-muted-foreground py-2">No user accounts upstream.</div>
      ) : (
        <table className="w-full text-[12px]" data-testid="indie-users-table">
          <thead>
            <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground">
              <th className="py-2 pr-3">Username</th>
              <th className="py-2 pr-3">Email</th>
              <th className="py-2 pr-3">Role</th>
              <th className="py-2 pr-3">Joined</th>
              <th className="py-2 pr-3">Set role</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const uid = u.id || u._id;
              return (
                <tr key={uid} data-testid={`indie-user-row-${uid}`}
                    className="border-b border-border/60">
                  <td className="py-2 pr-3 font-medium">{u.username || u.pen_name || '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground text-[11px]">{u.email || '—'}</td>
                  <td className="py-2 pr-3">
                    <span className="rounded-sm bg-secondary px-1.5 py-0.5 font-mono text-[10.5px]">
                      {u.role || 'reader'}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground font-mono text-[10.5px]">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 pr-3">
                    <select
                      defaultValue={u.role || 'reader'}
                      disabled={roleEditing[uid]}
                      onChange={(e) => setRole(uid, e.target.value)}
                      data-testid={`indie-user-role-${uid}`}
                      className="h-6 rounded-sm border border-border bg-background px-1 text-[11px]"
                    >
                      {['reader','author','admin'].map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ─── Panel ───────────────────────────────────────────────────────── */
export default function IndieReaderAdminPanel() {
  const [selected, setSelected] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  return (
    <div className="space-y-4" data-testid="indie-reader-admin-panel">
      <DiagnosticsCard />
      <AlertsCard />
      <AuthorRequestsCard />
      <BooksCard selectedId={selected?.id} onSelect={setSelected}
                 refreshTick={refreshTick} />
      <ChaptersCard book={selected} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <UploadCard kind="cover" book={selected}
                    onAttachedToBook={() => setRefreshTick((n) => n + 1)} />
        <UploadCard kind="portrait" book={selected} />
      </div>
      <BulkImportCard book={selected} />
      <UsersCard />
    </div>
  );
}
